import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateUtc } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

/**
 * Read-only view of a grantor's account, used by the grantee when an
 * AccountAuthorization is ACTIVE.
 */
const GrantorAccountView = ({ grantorId, onExit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeInv, setActiveInv] = useState(null);
  const [accruals, setAccruals] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [section, setSection] = useState('overview');

  useEffect(() => { load(); }, [grantorId]);

  useEffect(() => {
    if (activeInv) loadInvestmentDetails(activeInv);
  }, [activeInv]);

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, docsRes] = await Promise.all([
        axios.get(`${API}/grantor/${grantorId}/dashboard`),
        axios.get(`${API}/grantor/${grantorId}/documents`).catch(() => ({ data: [] })),
      ]);
      setData(dashRes.data);
      setDocuments(docsRes.data || []);
      const firstInv = dashRes.data.investments?.[0];
      if (firstInv) setActiveInv(firstInv.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zugriff verweigert');
      onExit?.();
    } finally {
      setLoading(false);
    }
  };

  const loadInvestmentDetails = async (invId) => {
    try {
      const [accRes, payRes] = await Promise.all([
        axios.get(`${API}/grantor/${grantorId}/investments/${invId}/accruals`),
        axios.get(`${API}/grantor/${grantorId}/investments/${invId}/payouts`),
      ]);
      setAccruals(accRes.data || []);
      setPayouts(payRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <div className="card text-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
      </div>
    );
  }

  if (!data) return null;

  const investments = data.investments || [];
  const currentInv = investments.find((i) => i.id === activeInv);

  return (
    <div className="space-y-6" data-testid="grantor-account-view">
      <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4 flex justify-between items-start flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-700 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <div>
            <p className="text-sm font-semibold text-amber-900">
              Sie betrachten gerade das Konto von {data.user.name || data.user.email}
            </p>
            <p className="text-xs text-amber-800">
              Nur Lesezugriff – Änderungen oder Auszahlungsanforderungen sind nicht möglich.
            </p>
          </div>
        </div>
        <button onClick={onExit} className="btn-secondary text-xs" data-testid="exit-grantor-view-button">
          Zurück zu meinem Konto
        </button>
      </div>

      <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b">
        {[
          { id: 'overview', label: 'Übersicht' },
          { id: 'accruals', label: 'Zinsen' },
          { id: 'payouts', label: 'Auszahlungen' },
          { id: 'documents', label: 'Dokumente' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
              section === t.id ? 'text-white bg-primary' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid={`grantor-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {section === 'overview' && (
        <div className="space-y-4">
          <div className="card">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Vollmachtgeber</p>
                <p className="font-semibold">{data.user.name}</p>
                <p className="text-xs text-muted-foreground">{data.user.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Anzahl Investments</p>
                <p className="text-2xl font-bold">{investments.length}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Gesamteinlage</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(data.total_invested_eur)}</p>
              </div>
            </div>
          </div>

          {investments.length === 0 ? (
            <div className="card text-center py-12">
              <p className="text-muted-foreground">Keine Investments vorhanden.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {investments.map((inv) => (
                <div
                  key={inv.id}
                  className={`card cursor-pointer transition ${
                    inv.id === activeInv ? 'ring-2 ring-primary' : 'hover:shadow-md'
                  }`}
                  onClick={() => setActiveInv(inv.id)}
                  data-testid={`grantor-investment-${inv.id}`}
                >
                  <div className="flex justify-between items-start flex-wrap gap-3">
                    <div>
                      <p className="font-medium">{inv.product_code} · {(inv.apr * 100).toFixed(1)}% p.a.</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Beginn: {inv.interest_start_date ? formatDateUtc(inv.interest_start_date) : '—'}
                        {inv.duration_months ? ` · Laufzeit: ${inv.duration_months} Monate` : ' · Unbefristet'}
                      </p>
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(inv.principal_eur)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'accruals' && (
        <div className="card">
          {investments.length > 1 && <InvSwitcher list={investments} active={activeInv} setActive={setActiveInv} />}
          <h3 className="font-semibold mb-3">Zinsen {currentInv ? `– ${currentInv.product_code}` : ''}</h3>
          {accruals.length === 0 ? (
            <p className="text-muted-foreground text-sm">Noch keine Zinsen vorhanden.</p>
          ) : (
            <div className="space-y-2">
              {accruals.map((a) => (
                <div key={a.id} className="flex justify-between p-3 bg-background-subtle rounded text-sm" data-testid={`grantor-accrual-${a.id}`}>
                  <span>{formatDateUtc(a.period_start)} – {formatDateUtc(a.period_end)}</span>
                  <span className="font-semibold text-success">+{formatCurrency(a.interest_amount_eur)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'payouts' && (
        <div className="card">
          {investments.length > 1 && <InvSwitcher list={investments} active={activeInv} setActive={setActiveInv} />}
          <h3 className="font-semibold mb-3">Auszahlungen {currentInv ? `– ${currentInv.product_code}` : ''}</h3>
          {payouts.length === 0 ? (
            <p className="text-muted-foreground text-sm">Noch keine Auszahlungen.</p>
          ) : (
            <div className="space-y-2">
              {payouts.map((p) => (
                <div key={p.id} className="flex justify-between items-center p-3 bg-background-subtle rounded text-sm" data-testid={`grantor-payout-${p.id}`}>
                  <div>
                    <p className="font-semibold">{formatCurrency(p.amount_eur)}</p>
                    <p className="text-xs text-muted-foreground">Fällig: {formatDateUtc(p.due_date)}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded ${
                    p.status === 'confirmed' ? 'bg-success text-white' :
                    p.status === 'due' ? 'bg-warning text-white' : 'bg-gray-300 text-gray-700'
                  }`}>
                    {p.status === 'confirmed' ? 'Bestätigt' : p.status === 'due' ? 'Fällig' : p.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === 'documents' && (
        <div className="card">
          <h3 className="font-semibold mb-3">Dokumente</h3>
          {documents.length === 0 ? (
            <p className="text-muted-foreground text-sm">Keine Dokumente vorhanden.</p>
          ) : (
            <div className="grid gap-2">
              {documents.map((d) => (
                <div key={d.id} className="flex justify-between items-center p-3 bg-background-subtle rounded" data-testid={`grantor-document-${d.id}`}>
                  <div>
                    <p className="font-medium text-sm">{d.title}</p>
                    <p className="text-xs text-muted-foreground">{formatDateUtc(d.created_at)}</p>
                  </div>
                  <a
                    href={`${API}/grantor/${grantorId}/documents/${d.id}/download`}
                    target="_blank" rel="noopener noreferrer"
                    className="text-accent hover:underline text-sm"
                    data-testid={`grantor-download-doc-${d.id}`}
                  >
                    Herunterladen
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const InvSwitcher = ({ list, active, setActive }) => (
  <div className="flex gap-2 mb-3 flex-wrap">
    {list.map((inv) => (
      <button
        key={inv.id}
        onClick={() => setActive(inv.id)}
        className={`text-xs px-3 py-1 rounded-full ${
          inv.id === active ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        {inv.product_code} · {(inv.apr * 100).toFixed(1)}%
      </button>
    ))}
  </div>
);

export default GrantorAccountView;
